import * as React from "react";
import flatpickr from "flatpickr";
import { flushSync } from "react-dom";
import type { Instance, Options } from "flatpickr";
import { useCalendarContainer } from "@/shadow/ShadowRootContext";

/**
 * flatpickr's built-in positioning assumes the calendar is absolutely positioned relative to the page (it uses
 * `pageXOffset` + viewport rects). Inside the funnel's shadow root the calendar is positioned relative to
 * `calendarContainer` instead (a zero-size `position: relative` element), so the offsets have to be computed from it.
 */
const positionCalendar = (preferred: string) => (self: Instance, customElement?: HTMLElement) => {
  const cal = self.calendarContainer;
  const origin = cal?.parentElement;
  if (!cal || !origin) return;
  const input = customElement ?? self._positionElement;
  const inputRect = input.getBoundingClientRect();
  const originRect = origin.getBoundingClientRect();
  const calHeight = cal.offsetHeight;
  const calWidth = cal.offsetWidth;
  const vertical = preferred.split(" ")[0];
  const showOnTop =
    vertical === "above" || (vertical !== "below" && window.innerHeight - inputRect.bottom < calHeight && inputRect.top > calHeight);
  const viewportWidth = document.documentElement.clientWidth;
  const left = Math.max(0, Math.min(inputRect.left, viewportWidth - calWidth));
  cal.classList.toggle("arrowTop", !showOnTop);
  cal.classList.toggle("arrowBottom", showOnTop);
  cal.classList.add("arrowLeft");
  cal.classList.remove("arrowCenter", "arrowRight", "rightMost", "centerMost");
  cal.style.top = `${(showOnTop ? inputRect.top - calHeight - 2 : inputRect.bottom + 2) - originRect.top}px`;
  cal.style.left = `${left - originRect.left}px`;
  cal.style.right = "auto";
};

/**
 * Wraps vanilla `flatpickr` directly instead of via `react-flatpickr`. The latter's internal
 * memoization is fundamentally broken — it spreads its *entire* incoming props object every
 * render (`{...props}`), so the props bag it diffs against is a fresh object reference on every
 * single render of the wrapping component, regardless of whether any individual value actually
 * changed. That makes its create/destroy effect rerun (fully tearing down and recreating the
 * flatpickr instance, including its cloned `altInput`) far more often than necessary — e.g. once
 * per keystroke anywhere else in the form. Under React 19 that churn could race with the
 * browser's own handling of the native input/blur event that triggered it, leaving the
 * should-be-hidden original `<input>` visible next to the altInput — i.e. the date field
 * rendering twice (see BirthDateInput/StartOfInsuranceInput/BirthdateField).
 *
 * This hook only recreates the flatpickr instance when `options` itself changes reference (so
 * callers must memoize it — see `flatpickrOptions` in each field), and syncs the selected value
 * via flatpickr's own `setDate()` API otherwise, which updates in place without touching the DOM
 * structure at all.
 */
export function useFlatpickrField(
  options: Options,
  value: Date | undefined,
  onChange: (dates: Date[], dateStr: string, instance: Instance) => void
) {
  // The calendar must live inside the funnel's shadow root, otherwise none of the funnel CSS applies to it.
  const calendarContainer = useCalendarContainer();
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const instanceRef = React.useRef<Instance | null>(null);
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;

  React.useEffect(() => {
    if (!inputRef.current) return;
    const instance = flatpickr(inputRef.current, {
      appendTo: calendarContainer,
      position: calendarContainer ? positionCalendar(typeof options.position === "string" ? options.position : "auto") : "auto",
      ...options,
      defaultDate: value,
      // flatpickr commits a typed date from its own `blur` listener, i.e. outside of any React event. Without
      // flushing, the state update can still be pending when the click that caused the blur (e.g. on the submit
      // button) is handled, and the handler would then validate with the old, empty date.
      onChange: (dates, dateStr, inst) => flushSync(() => onChangeRef.current(dates, dateStr, inst)),
    });
    instanceRef.current = instance;
    return () => {
      instance.destroy();
      instanceRef.current = null;
    };
    // `value` is intentionally excluded — it only seeds the initial `defaultDate`, subsequent
    // changes are synced imperatively below via `setDate()` rather than recreating the instance.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, calendarContainer]);

  React.useEffect(() => {
    const instance = instanceRef.current;
    if (!instance) return;
    if (instance.selectedDates[0]?.getTime() !== value?.getTime()) {
      instance.setDate(value ?? null, false);
    }
  }, [value]);

  return { inputRef, instanceRef };
}
